import DocumentService from '../../api/documents';
import Vue from 'vue';
import filteringHelper from 'src/services/filteringHelper';
import _ from 'lodash';

// Initial state
const initialState = () => {
  return {
    documentsData: {},
    pagination: {
      publication: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Relevance',
      },
      patent: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Relevance',
      },
      institution: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
      funder: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
      author: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
      inventor: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
      assignee: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
      bluelens: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
      sanction: {
        sortBy: 'desc',
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sort: 'Alphabetical',
      },
    },
    docLoader: {
      publication: true,
      patent: true,
      institution: true,
      funder: true,
      author: true,
      inventor: true,
      assignee: true,
      bluelens: true,
      sanction: true,
    },
    entityDocuments: [],
    entityDocLoader: true,
    entityPagination: {
      sortBy: 'desc',
      descending: false,
      page: 1,
      rowsPerPage: 10,
      sort: 'Relevance',
    },
    // "Merged view" checkbox selections, keyed by source like documentsData/pagination. Each entry is an array of
    // { entity_id, entity_type, entity_name } for hits the user has checked within that tab.
    selectedIds: {},
    // Person and institution scans' entity tabs, keyed by source: the results search box text and whether "Show
    // selected" limits the list to the selections above
    listSearch: {},
    showSelected: {},
  };
};

const state = initialState();

const getters = {
  getDocumentsData: (state) => {
    return state.documentsData;
  },
  getPagination: (state) => {
    return state.pagination;
  },
  docLoader: (state) => {
    return state.docLoader;
  },
  getEntityDocuments: (state) => {
    return state.entityDocuments;
  },
  getEntityPagination: (state) => {
    return state.entityPagination;
  },
  entityDocLoader: (state) => {
    return state.entityDocLoader;
  },
  getSelectedIds: (state) => {
    return state.selectedIds;
  },
  listSearch: (state) => state.listSearch,
  showSelected: (state) => state.showSelected,
};

const actions = {
  async fetchDocuments({ commit, rootGetters, state, dispatch }, payload) {
    // Set source for elasticsearch index by what is included in the payload --OR-- whatever the activeDataSource is
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];

    commit('setDocumentLoader', { source, data: true });

    // Get scan items
    const {
      query,
      query_elements,
      version_id,
      scan_id,
      user_id,
      name,
      description,
    } = rootGetters['scans/getScanDetails'];
    // Get scan type
    const scanType = rootGetters['scans/scanType'];
    // Set pagination to the value for the current source
    const pagination = state.pagination[source];
    // Set filters to null by default
    let filters = null;
    // Set query_builder to query by default
    let query_builder = query;
    // Check if there is data in currentFiltering for the passed-in source --OR-- activeDataSource
    if (
      rootGetters['filters/currentFiltering'][source] &&
      Object.keys(rootGetters['filters/currentFiltering'][source]).length
    ) {
      // Current data source has applied filters, set them to filters var
      filters = Object.assign(
        {},
        rootGetters['filters/currentFiltering'][source]
      );

      // Process filters based on scan type
      if (scanType !== 'semantic') {
        // Check if search_within_results is included in filters object
        if (filters.search_within_results) {
          // Call helper function to add search within results to query
          query_builder = filteringHelper.filterResultsText(
            query,
            filters.search_within_results
          );
        }
      } else {
        // Check if positive or negative filters are included in filters object
        if (filters.positive || filters.negative) {
          // Call helper function to add positive and negative filters to query
          query_builder = filteringHelper.filterSemantic(query_builder, {
            positive: filters.positive,
            negative: filters.negative,
          });
        }
      }
    }

    const skip_docs = payload && payload.skip_docs ? payload.skip_docs : false;
    const skip_filters =
      payload && payload.skip_filters ? payload.skip_filters : false;

    // Package up the request for the backend
    const request = {
      query: query_builder,
      scan_id: version_id,
      scan_type: scanType,
      pagination,
      filters,
      skip_aggs: true,
      source,
      scan_category: rootGetters['scans/scanCategory'],
      entity_search: state.listSearch[source] || null,
      selected_ids: state.showSelected[source]
        ? (state.selectedIds[source] || []).map((e) => e.entity_id)
        : null,
    };

    if (!skip_filters) {
      // When paginating, skip_filters is set to true as we don't need to re-fetch filters
      let _filtersPayload = {
        ...request,
        skip_aggs: false,
        createScanVersion: payload.createScanVersion ? true : false, // If createScanVersion = true is included in incoming payload to fetchDocuments, then this isn't an initial filters fetch, this is caused by the user updating the global filters. When the user changes the global filters, we want to create a new scan version.
      };
      // Call fetchFilters in Filters module (pass in {root: true}) because that's where we want the global filter aggregation data stored
      dispatch('filters/fetchFilters', _filtersPayload, { root: true });
    }

    if (!skip_docs) {
      // If initial scan creation serves documents, then we don't have to make this call
      const paginationService = new DocumentService();
      let { documents, doc_count, error } =
        await paginationService.fetchDocuments(request);

      if (!error) {
        const _payload = {
          source,
          data: documents,
        };
        // Commit document data from elasticsearch
        commit('setDocumentsData', _payload);
        // Set pagination data using Vue.set to trigger reactivity
        Vue.set(state.pagination[source], 'rowsNumber', doc_count);

        // Now that we have the doc_count we can...
        if (payload.createScanVersion) {
          // Build payload to pass to Scan service to create a new scan version
          const _versionPayload = {
            scan_type: rootGetters['scans/scanType'],
            scan_category: rootGetters['scans/scanCategory'],
            scan_id,
            current_version_id: version_id,
            user_id,
            name,
            query,
            query_elements,
            description,
            // Below properties are used to update filter history when a new scan version is created
            source: payload.source,
            filters: filters ? filters : {},
            active_filter_set_id: payload.activeFilterSetId || null,
            filter_set_doc_count: doc_count,
          };
          // Dispatch createScanVersion action in Scans module
          dispatch('scans/createScanVersion', _versionPayload, { root: true });
        }
        if (payload.getFilterHistoryDocCount) {
          // Update the active filter set doc_count if necessary - the scan has no filter set history, i.e. the scan was created before filter history implementation.
          dispatch(
            'filters/updateFilterSetDocCount',
            { source },
            { root: true }
          );
        }
      }
    }

    commit('setDocumentLoader', { source, data: false });
    return true;
  },

  paginatingDocuments({ commit, dispatch, rootGetters }, payload) {
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];
    // commit pagination
    let pagination = {};
    pagination.page = payload.page;
    pagination.rowsPerPage = payload.rowsPerPage;
    pagination.sortBy = payload.sortBy;

    commit('setPagination', { source: source, data: payload });
    dispatch('fetchDocuments', { skip_aggs: true, skip_filters: true });
  },
  // A tab's results search box ({ source, search }) or "Show selected" ({ source, showSelected }) changed: list from
  // page 1 again
  setListFilter({ commit, dispatch, state }, { source, ...change }) {
    if ('search' in change) commit('setListSearch', { source, data: change.search });
    if ('showSelected' in change) commit('setShowSelected', { source, data: change.showSelected });
    commit('setPagination', { source, data: { ...state.pagination[source], page: 1 } });
    dispatch('fetchDocuments', { source, skip_aggs: true, skip_filters: true });
  },
  clearData({ commit }) {
    commit('clearStore');
  },
  async fetchEntityDocuments({ commit, rootGetters }, payload) {
    // Fetch documents for the Entity Drill Down component -- These are separate from the 'regular' documents in the Documents tab
    commit('setEntityDocumentLoader', true);
    const { version_id } = rootGetters['scans/getScanDetails'];
    const entityConfig = _.cloneDeep(
      rootGetters['entity/entityDrillDownConfig']
    );
    const entityDocumentFilters = _.cloneDeep(
      rootGetters['entity/entityDocumentFilters']
    );
    const pagination = state.entityPagination;
    // Set source for elasticsearch index by what is included in the payload --OR-- whatever the entityConfig.source is
    const source =
      payload && payload.source ? payload.source : entityConfig.source;

    // Get scan type from Scans module, default to semantic if not set, i.e., if this action is dispatched from outside the scan context
    const scanType = rootGetters['scans/scanType']
      ? rootGetters['scans/scanType']
      : 'boolean';
    const queryScope = entityConfig.queryScope;
    const ENTITY_LOOKUP = {
      author: 'author_ids.keyword',
      institution: 'author_affiliation_ids.keyword',
      institution_types: 'affiliation_types.keyword',
      country:
        source === 'publication' || source === 'fundingPublication'
          ? 'author_affiliation_country_codes.keyword'
          : entityConfig.entitySubType === 'assignee'
          ? 'assignee_country_codes.keyword'
          : 'inventor_country_codes.keyword', // If patent source, field is determined by entitySubType
      state:
        source === 'publication'
          ? 'author_affiliation_state_codes.keyword'
          : entityConfig.entitySubType === 'assignee'
          ? 'assignee_state_codes.keyword'
          : 'inventor_state_codes.keyword', // If patent source, field is determined by entitySubType
      assignee: 'assignee_ids',
      authority: 'authority_code.keyword',
      cpc: 'cpc_codes.keyword',
      family: 'patent_family_id',
      inventor: 'inventor_ids',
      funder: 'funder_ids.keyword',
      recipient: 'recipient_ids.keyword',
      psc: 'psc_codes.keyword',
    };
    const YEAR_FIELD_LOOKUP = {
      publication: 'publication_year',
      patent: 'filing_year',
      funding: 'funding_us_fiscal_year',
      fundingPublication: 'publication_year',
    };
    let filters = {};
    let entityQuery = _.cloneDeep(entityConfig.query);
    // Create a var to use if partnership entity drill down query scope is toggled to off
    let nullQuery = {};
    // Compute current 5 year range and return as an object to be used for filtering
    const currentYear = new Date().getFullYear();
    const yearRange = {
      min: currentYear - 4,
      max: currentYear,
    };
    // Create an array to hold fields that must match for semantic scans, to be treated as must/AND
    const must_match = [];
    // Check if this is a single entity, a merged (disambiguated-duplicate) selection, or an entity partnership.
    // mergeMode uses the same array/terms (OR/union) filter-building branch as a single entity, since multiple ids
    // here represent the SAME entity rather than entities that must co-occur (partnership/AND semantics below).
    if (entityConfig.entityId.length === 1 || entityConfig.mergeMode) {
      // This is a single entity (or a merged set of duplicate hits) - Build filter object to include the individual entity/entities we want to filter by
      // Include a publication_year range in the case of countries and states
      switch (entityConfig.entityType) {
        case 'author':
          filters = { author_ids: [...entityConfig.entityId] };
          break;
        case 'institution':
          filters = { author_affiliation_ids: [...entityConfig.entityId] };
          break;
        case 'institution_types':
          filters = {
            affiliation_types: [...entityConfig.entityId],
            publication_year: [{ min: yearRange.min, max: yearRange.max }],
          };
          break;
        case 'funder':
          filters = { funder_ids: [...entityConfig.entityId] };
          break;
        case 'recipient':
          filters = { recipient_ids: [...entityConfig.entityId] };
          break;
        case 'country':
          if (source === 'publication' || source === 'fundingPublication') {
            filters = {
              author_affiliation_country_codes: [...entityConfig.entityId],
              publication_year: [{ min: yearRange.min, max: yearRange.max }],
            };
          } else if (source === 'patent') {
            // Check the subType to determine if we're filtering by assignee or inventor countries
            if (entityConfig.entitySubType === 'assignee') {
              filters = {
                filing_year: [{ min: yearRange.min, max: yearRange.max }],
                assignee_country_codes: [...entityConfig.entityId],
              };
            } else if (entityConfig.entitySubType === 'inventor') {
              filters = {
                filing_year: [{ min: yearRange.min, max: yearRange.max }],
                inventor_country_codes: [...entityConfig.entityId],
              };
            }
          } else if (source === 'funding') {
            if (entityConfig.entitySubType === 'recipient') {
              filters = {
                funding_us_fiscal_year: [
                  { min: yearRange.min, max: yearRange.max },
                ],
                recipient_countries: [...entityConfig.entityId],
              };
            } else if (entityConfig.entitySubType === 'funder') {
              filters = {
                funding_us_fiscal_year: [
                  { min: yearRange.min, max: yearRange.max },
                ],
                funder_countries: [...entityConfig.entityId],
              };
            }
          }
          break;
        case 'state':
          if (source === 'publication') {
            filters = {
              author_affiliation_state_codes: [...entityConfig.entityId],
              publication_year: [{ min: yearRange.min, max: yearRange.max }],
            };
          } else {
            // Check the subType to determine if we're filtering by assignee or inventor states
            if (entityConfig.entitySubType === 'assignee') {
              filters = {
                filing_year: [{ min: yearRange.min, max: yearRange.max }],
                assignee_state_codes: [...entityConfig.entityId],
              };
            } else if (entityConfig.entitySubType === 'inventor') {
              filters = {
                filing_year: [{ min: yearRange.min, max: yearRange.max }],
                inventor_state_codes: [...entityConfig.entityId],
              };
            }
          }
          break;
        case 'assignee':
          filters = { assignee_ids: [...entityConfig.entityId] };
          break;
        case 'authority':
          filters = {
            authority_code: [...entityConfig.entityId],
            filing_year: [{ min: yearRange.min, max: yearRange.max }],
          };
          break;
        case 'cpc':
          filters = {
            cpc_codes: [...entityConfig.entityId],
            filing_year: [{ min: yearRange.min, max: yearRange.max }],
          };
          break;
        case 'family':
          filters = { patent_family_id: [...entityConfig.entityId] };
          break;
        case 'inventor':
          filters = { inventor_ids: [...entityConfig.entityId] };
          break;
        case 'concept':
          if (scanType !== 'semantic') {
            // If the entity type is a concept, we don't use a filter within the elasticsearch query, we need to build a nested path query within the bool block to return results by by concept.id
            // Build must block to insert into query
            const _must = [
              {
                nested: {
                  path: 'concepts',
                  query: {
                    match: {
                      'concepts.id': entityConfig.entityId[0],
                    },
                  },
                },
              },
            ];
            if (entityQuery) {
              entityQuery.query.bool.must = _must;
            } else {
              // Query scope toggle is OFF - Still need to use nested path query to only get docs associated with the concept ID
              nullQuery = {
                query: {
                  bool: {
                    must: _must,
                  },
                },
              };
            }
          } else {
            // Add concepts filter with nested name format to filters object
            filters = {
              'concepts.id': [entityConfig.entityId[0]],
            };
            // Add filter to must match
            must_match.push('concepts.id');
          }
          // If query scope is off, ie we're getting ALL results not just those for the scan, limit the results to the current 5 year range
          if (!entityConfig.queryScope) {
            filters[YEAR_FIELD_LOOKUP[source]] = [
              { min: yearRange.min, max: yearRange.max },
            ];
          }
          break;
        case 'psc':
          filters = {
            psc_codes: [...entityConfig.entityId],
          };
          break;
        default:
          filters = {};
      }

      // Add year range filter to filters object if it doesn't already exist and is present in the entityConfig
      if (entityConfig.yearRange && !filters[YEAR_FIELD_LOOKUP[source]]) {
        filters[YEAR_FIELD_LOOKUP[source]] = [
          {
            min: entityConfig.yearRange.min,
            max: entityConfig.yearRange.max,
          },
        ];
      }
    } else {
      // This is an entity partnership - Build must block to get only documents that contain BOTH entity IDs
      let _type = ENTITY_LOOKUP[entityConfig.entityType];
      if (scanType !== 'semantic') {
        let mustBlock = [];
        // Add a match object for each ID
        entityConfig.entityId.forEach((entity) => {
          const prop = _type;
          const _match = {
            match: {
              [prop]: entity,
            },
          };
          // Add must object to mustBlock array
          mustBlock.push(_match);
        });

        const filters = [];
        if (entityConfig.yearRange) {
          // Add year range filter to filters array
          filters.push({
            range: {
              [YEAR_FIELD_LOOKUP[source]]: {
                gte: entityConfig.yearRange.min,
                lte: entityConfig.yearRange.max,
              },
            },
          });
        }

        // Check to see if a non-null query was passed in
        if (entityConfig.query) {
          // A query non-null query is being used, add must block and year range filter
          // Add mustBlock to query object
          entityQuery.query.bool.must = mustBlock;
          // Add year range filter to query object if present
          entityQuery.query.bool.filter = filters;
        } else {
          // Null query passed in -- Query scope toggle set to off in a partnership entity drill down modal
          // Build a query object to use the mustBlock built for this partnership, and the year range filter if present
          nullQuery.query = {
            bool: {
              must: mustBlock,
              ...(entityConfig.entityType !== 'author' && {
                filter: filters,
              }),
            },
          };
        }
      } else {
        // Semantic scan
        // Remove .keyword from _type
        _type = _type.split('.')[0];

        // Add yearRange and entity IDs to filters object
        filters = {
          ...filters,
          // ...yearRangeFilter,
          [_type]: [...entityConfig.entityId],
        };

        if (entityConfig.yearRange) {
          // Add year range filter to filters object
          filters[YEAR_FIELD_LOOKUP[source]] = [
            {
              min: entityConfig.yearRange.min,
              max: entityConfig.yearRange.max,
            },
          ];
        }

        // Add field to must_match array
        must_match.push(_type);
      }
    }

    if (source === 'funding') {
      // Filter documents by funding categories we display
      filters.category = [
        'basic_research',
        'early_stage',
        'late_stage',
        'commercialization',
      ];
    }

    if (source === 'fundingPublication') {
      // Filter documents to those with funders
      filters.exists = ['funder_ids'];
    }

    // Process entityDocumentFilters
    if (Object.keys(entityDocumentFilters).length) {
      if (scanType !== 'semantic') {
        // Get the must block from the query if there is one, otherwise create a new one
        const mustBlock =
          entityQuery && entityQuery.query.bool.must
            ? entityQuery.query.bool.must
            : nullQuery.query
            ? nullQuery.query.bool.must
            : [];
        // Add a bool query with should array to mustBlock if it doesn't exist already
        if (
          !mustBlock[mustBlock.length - 1] ||
          !mustBlock[mustBlock.length - 1].bool
        ) {
          mustBlock.push({
            bool: { should: [], minimum_should_match: 1 },
          });
        }
        // Add entityDocumentFilters to mustBlock
        Object.keys(entityDocumentFilters).forEach((filterType) => {
          if (entityDocumentFilters[filterType].length) {
            if (
              entityConfig.entityType === 'author' &&
              filterType === 'institution'
            ) {
              // If the entity type is author and the filter type is institution, we're filtering on author affiliations. Add a nested query to the should array in order to query the nested author_affiliations field for documents where the institution id(s) being filtered on map to the author in the drill down, i.e., docs where the institutions are specifically associated with the author rather than all documents containing the institutions
              mustBlock[mustBlock.length - 1].bool.should.push({
                nested: {
                  path: 'author_affiliations',
                  query: {
                    bool: {
                      must: [
                        {
                          terms: {
                            'author_affiliations.author_id':
                              entityConfig.entityId,
                          },
                        },
                        {
                          terms: {
                            'author_affiliations.affiliation_id': [],
                          },
                        },
                      ],
                    },
                  },
                },
              });
            }
            if (
              entityConfig.entityType === 'institution' &&
              filterType === 'author'
            ) {
              // Add a nested query to the should array in order to query the nested author_affiliations field for documents where the author id(s) being filtered on map to the institution in the drill down, i.e., docs where the authors are specifically associated with the institution rather than all documents containing the authors
              mustBlock[mustBlock.length - 1].bool.should.push({
                nested: {
                  path: 'author_affiliations',
                  query: {
                    bool: {
                      must: [
                        {
                          terms: {
                            'author_affiliations.affiliation_id':
                              entityConfig.entityId,
                          },
                        },
                        {
                          terms: {
                            'author_affiliations.author_id': [],
                          },
                        },
                      ],
                    },
                  },
                },
              });
            }
            // Loop over each filter
            entityDocumentFilters[filterType].forEach((filter) => {
              if (
                entityConfig.entityType === 'author' &&
                filterType === 'institution'
              ) {
                // Filtering author affiliations, find the nested query in the should array
                const nestedQuery = mustBlock[
                  mustBlock.length - 1
                ].bool.should.find((item) => item.nested);
                if (nestedQuery) {
                  // Add the institution ID to the nested query terms array
                  nestedQuery.nested.query.bool.must[1].terms[
                    'author_affiliations.affiliation_id'
                  ].push(filter.id);
                }
              } else if (
                entityConfig.entityType === 'institution' &&
                filterType === 'author'
              ) {
                // Filtering author affiliations, find the nested query in the should array
                const nestedQuery = mustBlock[
                  mustBlock.length - 1
                ].bool.should.find((item) => item.nested);
                if (nestedQuery) {
                  // Add the author ID to the nested query terms array
                  nestedQuery.nested.query.bool.must[1].terms[
                    'author_affiliations.author_id'
                  ].push(filter.id);
                }
              } else {
                // Add a term query to the should array
                const prop = ENTITY_LOOKUP[filterType];
                const _term = {
                  term: {
                    [prop]: filter.id,
                  },
                };
                mustBlock[mustBlock.length - 1].bool.should.push(_term);
              }
            });
          }
        });
        // Add mustBlock to query
        if (mustBlock.length) {
          if (entityQuery) {
            // Add mustBlock to entityQuery
            entityQuery.query.bool.must = mustBlock;
          } else {
            // Check if nullQuery has a query object. If so, add mustBlock to it. If not, create a query object with a bool block and add mustBlock to it
            nullQuery.query && nullQuery.query.bool
              ? (nullQuery.query.bool.must = mustBlock)
              : (nullQuery.query = { bool: { must: mustBlock } });
          }
        }
      } else {
        // Semantic scan
        // Create a new raw_filters array in the filters object
        filters.raw_filters = [];
        if (entityConfig.entityType === 'concept') {
          // Add a nested filter for the concepts field to the raw_filters array
          filters.raw_filters.push({
            type: 'nested',
            path: 'concepts',
            subfields: [
              {
                field: 'id.keyword',
                values: [entityConfig.entityId[0]],
              },
            ],
            must_match: true, // Must match is true to be treated as an AND/must as we must include documents for the entity in the drill down
          });
          // Remove the concepts filter from the filters object as it's been added to the raw_filters array
          delete filters['concepts.id'];
        } else {
          // Get the type for the entity/entities in the drill down
          const _type = ENTITY_LOOKUP[entityConfig.entityType].split('.')[0];
          // Add a raw filter for the entity/entities in the drill down with a must_match of true, i.e., to be treated as an AND/must
          filters.raw_filters.push({
            type: 'terms',
            field: _type,
            values: entityConfig.entityId,
            must_match: true,
          });
          // Remove the filter type from the filters object as it's been added to the raw_filters array
          delete filters[_type];
        }

        // Add entity document filters to filters object
        Object.keys(entityDocumentFilters).forEach((filterType) => {
          if (entityDocumentFilters[filterType].length) {
            /// Remove .keyword from filter type
            const _type = ENTITY_LOOKUP[filterType].split('.')[0];
            const ids = entityDocumentFilters[filterType].map(
              (entity) => entity.id
            );
            if (
              entityConfig.entityType === 'author' &&
              filterType === 'institution'
            ) {
              // Build a raw filter for the nested author_affiliations field to get institutions specifically associated with the author -- must_match is false to be treated as an OR/should
              filters.raw_filters.push({
                type: 'nested',
                path: 'author_affiliations',
                subfields: [
                  {
                    field: 'affiliation_id',
                    values: ids,
                  },
                  {
                    field: 'author_id',
                    values: entityConfig.entityId,
                  },
                ],
                must_match: false,
              });
            } else if (
              entityConfig.entityType === 'institution' &&
              filterType === 'author'
            ) {
              // Build a raw filter for the nested author_affiliations field to get authors specifically associated with the institution -- must_match is false to be treated as an OR/should
              filters.raw_filters.push({
                type: 'nested',
                path: 'author_affiliations',
                subfields: [
                  {
                    field: 'author_id',
                    values: ids,
                  },
                  {
                    field: 'affiliation_id',
                    values: entityConfig.entityId,
                  },
                ],
                must_match: false,
              });
            } else {
              // Build a raw terms filter with a must_match of false, i.e., to be treated as an OR/should
              filters.raw_filters.push({
                type: 'terms',
                field: _type,
                values: ids,
                must_match: false,
              });
            }
          }
        });
      }
    }

    // If no query scope and null query has no properties, set it to null
    if (!queryScope && !Object.keys(nullQuery).length) {
      nullQuery = null;
    }

    // Package up the request -- Use query stored in entityConfig. That value is controlled via the query scope toggle in the entity drill down component
    const request = {
      scan_id: version_id,
      scan_type: scanType,
      query: queryScope ? entityQuery : nullQuery,
      pagination,
      filters,
      skip_aggs: true,
      source,
      must_match,
    };

    const docService = new DocumentService();

    let { documents, doc_count, error } = await docService.fetchDocuments(
      request
    );
    if (!error) {
      // commit all data from endpoint

      // Use Vue.set for reactivity reasons
      Vue.set(state.entityPagination, 'rowsNumber', doc_count);
      commit('setEntityDocuments', documents);
    }
    commit('setEntityDocumentLoader', false);
  },
  paginatingEntityDocuments({ commit, dispatch }, payload) {
    // commit entityPagination
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];
    let entityPagination = {};
    entityPagination.page = payload.page;
    entityPagination.rowsPerPage = payload.rowsPerPage;
    entityPagination.sortBy = payload.sortBy;
    commit('setEntityPagination', payload);
    // dispatch fetchEntityDocuments
    dispatch('fetchEntityDocuments', { source });
  },
  resetEntityPagination({ commit }) {
    // Reset entity pagination to initial state
    commit('resetEntityPagination');
  },
  updatePagination({ commit, state, rootGetters }, payload) {
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];
    // Update pagination when search value changes - Set page back to 1 but keep current sort and rowsPerPage values
    const pagination = {
      page: 1,
      rowsPerPage: state.pagination[source].rowsPerPage,
      sort: state.pagination[source].sort,
      sortBy: state.pagination[source].sortBy,
    };
    commit('setPagination', { source: source, data: pagination });
  },
};

// mutations
const mutations = {
  setDocumentLoader(state, payload) {
    Vue.set(state.docLoader, payload.source, payload.data);
  },
  setDocumentsData(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.documentsData, payload.source, payload.data);
  },
  setPagination(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.pagination, payload.source, payload.data);
  },
  setListSearch(state, payload) {
    Vue.set(state.listSearch, payload.source, payload.data);
  },
  setShowSelected(state, payload) {
    Vue.set(state.showSelected, payload.source, payload.data);
  },
  setNumOfDocs(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.pagination[payload.source], 'rowsNumber', payload.data);
  },
  clearStore(state) {
    state = Object.assign(state, initialState());
  },
  setEntityDocuments(state, payload) {
    state.entityDocuments = payload;
  },
  setEntityPagination(state, payload) {
    state.entityPagination = payload;
  },
  setEntityDocumentLoader(state, payload) {
    state.entityDocLoader = payload;
  },
  resetEntityPagination(state) {
    state.entityPagination = {
      sortBy: 'desc',
      descending: false,
      page: 1,
      rowsPerPage: 10,
      sort: 'Relevance',
    };
  },
  // Bulk-replace the selected entities for a source, e.g. when hydrating from a saved scan or toggling a checkbox
  // (see scans/toggleMergedSelection, which computes the updated array and persists it to the backend)
  setSelectedEntities(state, payload) {
    Vue.set(state.selectedIds, payload.source, payload.data);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
